<template>
  <div class="assessment-layout">
    <!-- Add Sidebar component -->
    <Sidebar />
    
    <main class="assessment-content">
      <div class="header-row">
        <h1>Assessment Options</h1>
        <router-link 
          to="/assessment-options/create" 
          class="create-button"
        >
          Create New
        </router-link>
      </div>
      
      <loading-spinner v-if="isLoading" message="Loading assessment options..." />
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else>
        <div v-for="option in options" :key="option.id" class="option-item">
          <h3 class="question">
            {{ option.question }} ({{ option.question_ar }})
          </h3>
          <div class="values">
            <div v-for="value in option.assessment_options" :key="value.id" class="value-item">
              <span class="order">{{ value.order }}.</span>
              <template v-if="editingId === value.id">
                <input v-model="editingValues.value" class="edit-input" />
                <input v-model="editingValues.value_ar" class="edit-input" />
                <div class="edit-buttons">
                  <button @click="saveEdit(value.id)" class="save-button">Save</button>
                  <button @click="cancelEdit" class="cancel-button">Cancel</button>
                </div>
              </template>
              <template v-else>
                <span class="english">{{ value.value }}</span>
                <span class="arabic" v-if="value.value_ar">({{ value.value_ar }})</span>
                <div class="action-buttons">
                  <button @click="startEditing(value)" class="edit-button">Edit</button>
                  <button @click="deleteOption(value.id)" class="delete-button">Delete</button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Sidebar from '@/components/Sidebar.vue'

export default {
  name: 'AssessmentOptions',
  components: {
    Sidebar,
  },
  data() {
    return {
      options: [],
      editingId: null,
      editingValues: {
        value: '',
        value_ar: ''
      }
    }
  },
  computed: {
    ...mapGetters(['isLoading', 'errorMessage']),
    error() {
      return this.errorMessage
    }
  },
  methods: {
    ...mapActions(['fetchAssessmentOptions', 'updateAssessmentOption', 'deleteAssessmentOption', 'addNotification']),
    
    async loadAssessmentOptions() {
      try {
        const options = await this.fetchAssessmentOptions()
        
        // Group options by question
        const grouped = options.reduce((acc, item) => {
          const question = item.question
          if (!acc[question.id]) {
            acc[question.id] = {
              ...question,
              assessment_options: []
            }
          }
          acc[question.id].assessment_options.push(item)
          return acc
        }, {})

        // Sort options by order after grouping
        this.options = Object.values(grouped).map(q => ({
          ...q,
          assessment_options: q.assessment_options.sort((a, b) => a.order - b.order)
        }))
        
      } catch (err) {
        this.addNotification({
          type: 'error',
          message: `Failed to load assessment options: ${err.message}`
        })
      }
    },

    startEditing(option) {
      this.editingId = option.id
      this.editingValues = {
        value: option.value,
        value_ar: option.value_ar || ''
      }
    },

    cancelEdit() {
      this.editingId = null
      this.editingValues = { value: '', value_ar: '' }
    },

    async saveEdit(optionId) {
      try {
        await this.updateAssessmentOption({ 
          optionId, 
          optionData: {
            value: this.editingValues.value,
            value_ar: this.editingValues.value_ar
          }
        })

        // Update local state
        this.options = this.options.map(question => ({
          ...question,
          assessment_options: question.assessment_options.map(opt => 
            opt.id === optionId ? { 
              ...opt, 
              value: this.editingValues.value,
              value_ar: this.editingValues.value_ar
            } : opt
          )
        }))

        this.cancelEdit()
        
        this.addNotification({
          type: 'success',
          message: 'Assessment option updated successfully'
        })
      } catch (err) {
        this.addNotification({
          type: 'error',
          message: `Failed to update option: ${err.message}`
        })
      }
    },

    async deleteOption(optionId) {
      if (!confirm('Are you sure you want to delete this option?')) return
      
      try {
        await this.deleteAssessmentOption(optionId)

        // Remove deleted option from state
        this.options = this.options.map(question => ({
          ...question,
          assessment_options: question.assessment_options.filter(opt => opt.id !== optionId)
        })).filter(question => question.assessment_options.length > 0)
        
        this.addNotification({
          type: 'success',
          message: 'Assessment option deleted successfully'
        })
      } catch (err) {
        this.addNotification({
          type: 'error',
          message: `Failed to delete option: ${err.message}`
        })
      }
    }
  },
  mounted() {
    this.loadAssessmentOptions()
  }
}
</script>

<style scoped>
/* Add layout styles */
.assessment-layout {
  display: flex;
  min-height: 100vh;
}

.assessment-content {
  flex: 1;
  margin-left: 280px; /* Match sidebar width */
  padding: 2rem;
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Modify existing styles to match sidebar theme */
.assessment-content h1 {
  color: #1e293b;
  margin-bottom: 2rem;
  font-size: 2rem;
  font-weight: 600;
}

.option-item {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.question {
  color: #1e293b;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 500;
}

.value-item {
  padding: 0.75rem;
  margin: 0.5rem 0;
  background-color: #f8fafc;
  border-radius: 0.375rem;
}

.order {
  color: #3b82f6;
  font-weight: 600;
}

.arabic {
  color: #64748b;
}

.loading, .error {
  padding: 2rem;
  text-align: center;
  font-size: 1.125rem;
}

.error {
  color: #ef4444;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .assessment-content {
    margin-left: 0;
    padding: 1rem;
  }
  
  .assessment-layout {
    flex-direction: column;
  }
}

/* Add these styles */
.create-form {
  margin-bottom: 3rem;
  padding: 2rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.create-form h2 {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1e293b;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #64748b;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 1rem;
}

button[type="submit"] {
  background-color: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

button[type="submit"]:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
}

.success {
  color: #22c55e;
  margin-top: 1rem;
}

/* Add header row styles */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.create-button {
  background-color: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  text-decoration: none;
  transition: background-color 0.2s;
}

.create-button:hover {
  background-color: #2563eb;
}

/* Add these styles to the style section */
.edit-input {
  margin-right: 0.5rem;
  padding: 0.25rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
}

.edit-buttons {
  display: inline-block;
  margin-left: 1rem;
}

.edit-button, .save-button, .cancel-button, .delete-button {
  padding: 0.25rem 0.75rem;
  margin-left: 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: opacity 0.2s;
}

.edit-button {
  background-color: #006EFFFF;
  border: 1px solid #cbd5e1;
}

.save-button {
  background-color: #10b981;
  color: white;
  border: none;
}

.cancel-button {
  background-color: #ef4444;
  color: white;
  border: none;
}

.delete-button {
  background-color: #ef4444;
  color: white;
  border: none;
}

button:hover {
  opacity: 0.9;
}

.action-buttons {
  display: inline-block;
  margin-left: 0.5rem;
}
</style> 